import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { PricesFactor } from '@ui/features/Trade/types';
import { useGasToken } from '@ui/hooks/useGasToken';
import { Text } from '@ui/lib/park/ui/text';
import { OrderType } from '@ui/types/trade';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { Box, Divider, Flex, HStack } from 'styled-system/jsx';

import {
  useGetExecutionFeeInPayToken,
  useGetExecutionFeeUsd,
} from '../hooks/useGetExecutionFee';
import { useEstOpenLimitGasFee } from './TradeModule/hooks/useEstOpenLimitGasFee';
import { useEstOpenPositionGasFee } from './TradeModule/hooks/useEstOpenPositionGasFee';
import { useIsConfirmBtnDisabled } from './TradeModule/hooks/useIsConfirmBtnDisabled';
import { useOpenPositionFee } from './TradeModule/hooks/useOpenPositionFee';
import { useTotalOpenPositionFee } from './TradeModule/hooks/useTotalOpenPositionFee';
import { useCollateralToken } from './TradeModule/store/useCollateralToken';
import { useOrderType } from './TradeModule/store/useOrderType';

export const FeesBreakdown = ({ entryPrice, payTokenPrice }: PricesFactor) => {
  const { t } = useTranslation();

  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const openPositionFee = useOpenPositionFee();
  const executionFeeUsd = useGetExecutionFeeUsd();
  const executionFeedAmount = useGetExecutionFeeInPayToken();
  const gasToken = useGasToken();
  const orderType = useOrderType((s) => s._persistValue);
  const isMarket = orderType === OrderType.MARKET;
  const [isConfirmBtnDisabled] = useIsConfirmBtnDisabled();

  const estimateOpenPositionGasFee = useEstOpenPositionGasFee({
    disabled: !!isConfirmBtnDisabled,
  });

  const estimateOpenLimitGasFee = useEstOpenLimitGasFee({
    disabled: !!isConfirmBtnDisabled,
  });

  const totalOpenPositionFee = useTotalOpenPositionFee({});

  const currentTradePairInfo = useTradingPair((s) => s.useTradingPair());
  const isDegen = currentTradePairInfo.isDegenPair;

  const txnActionGas = isMarket
    ? estimateOpenPositionGasFee
    : estimateOpenLimitGasFee;

  const isDiscount = !openPositionFee?.value.final.eq(openPositionFee.value.base);

  return (
    <Flex
      flexDirection="column"
      gap="1"
      minW="64"
      color="greyWhite.lightGrey1"
      textStyle="text.xsmall"
      data-cy="FeesBreakdown"
    >
      {
        isDiscount && (
          <FeeRow
            lineThrough
            label={t('common.Open Position Fee')}
            value={openPositionFee?.value.base}
            usdValue={openPositionFee?.usd.base}
            displaySymbol={payToken?.displaySymbol || ''}
            isFree={isDegen}
          />
        )
      }
      <FeeRow
        label={isDiscount ? "" : t('common.Open Position Fee')}
        value={openPositionFee?.value.final}
        usdValue={openPositionFee?.usd.final}
        displaySymbol={payToken?.displaySymbol || ''}
        isFree={isDegen}
      />
      <FeeRow
        label={t('common.Execution fee')}
        value={executionFeedAmount}
        usdValue={executionFeeUsd}
        displaySymbol={payToken?.displaySymbol || ''}
        isFree={true}
      />
      <FeeRow
        label={t('common.Estimated Gas Fee')}
        value={txnActionGas?.gasInNativeToken}
        usdValue={txnActionGas?.gasInUsd}
        displaySymbol={gasToken.displaySymbol}
      />
      <Divider borderColor="greyWhite.outline" my={2} />
      <Flex
        justifyContent="space-between"
        width="100%"
        color="greyWhite.white"
        textStyle="headings.xxsmall"
      >
        <Text>{t('common.Total Fees')}</Text>
        <AmountUsd
          value={totalOpenPositionFee?.final}
          data-cy="TooltipTotalFees"
          data-cy-value={totalOpenPositionFee?.final}
        />
      </Flex>
    </Flex>
  );
};

const FeeRow = ({
  label,
  value,
  usdValue,
  displaySymbol,
  isFree = false,
  lineThrough = false,
}: {
  label: string;
  value: BigNumber | undefined;
  usdValue: BigNumber | undefined;
  displaySymbol: string;
  isFree?: boolean;
  lineThrough?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <HStack justifyContent="space-between" width="100%">
      <Text>{label}</Text>
      <Box data-cy={label.replace(/\s+/g, '')}>
        {isFree ? (
          <Text textStyle="text.xsmall" color="system.successLight">
            {t('common.Free')}
          </Text>
        ) : (
          <>
            <Amount
              textDecoration={lineThrough ? "line-through" : "none"}
              value={value}
              display="inline-block"
              opts={{ suffix: ' ' + displaySymbol }}
              data-cy="TokenAmount"
              data-cy-value={value}
            />{' '}
            (
            <AmountUsd
              textDecoration={lineThrough ? "line-through" : "none"}
              value={usdValue}
              display="inline-block"
              data-cy="UsdAmount"
              data-cy-value={usdValue}
            />
            )
          </>
        )}
      </Box>
    </HStack>
  );
};
